import {
  type BuiltTitle,
  buildTitle,
  formatTitleCount,
  formatTitleMinPrice,
  siteNamePart,
} from './titleTemplate'

// GG-10 titles, H1s and meta descriptions for home, /products and the
// furniture and fashion category pages. Other verticals keep their old copy.

export type PageSeo = {
  /** Full <title>; render with titleTemplate '%s' so no second suffix is added. */
  title: string
  h1: string
  description: string
}

export const FURNITURE_CATEGORY = 'Furniture'
export const FASHION_CATEGORY = 'Fashion & Apparel'

export const DESCRIPTION_MIN_LENGTH = 120
export const DESCRIPTION_MAX_LENGTH = 155

const toPageSeo = (built: BuiltTitle, description: string): PageSeo => ({
  title: built.title,
  h1: built.name,
  description,
})

export const HOME_SEO: PageSeo = {
  title: 'Dupe.com: Dupe Finder for Furniture, Fragrance & Fashion',
  // The name part ("Dupe.com") alone says nothing; the H1 carries the claim.
  h1: 'Dupe Finder for Furniture, Fragrance & Fashion',
  description:
    'Find dupes of the furniture, fragrance and fashion you love, and compare cheaper lookalikes from thousands of stores side by side.',
}

export const PRODUCTS_HUB_SEO: PageSeo = toPageSeo(
  buildTitle({
    name: 'Dupes by Category',
    parts: [{ text: ': Furniture, Fashion & Fragrance' }, siteNamePart(1)],
  }),
  'Shop dupes by category, from sofas, beds and dressers to dresses, shoes and perfume, with cheaper lookalikes from many stores side by side.'
)

export const FURNITURE_HUB_SEO: PageSeo = toPageSeo(
  buildTitle({
    name: 'Furniture Dupes',
    parts: [{ text: ': Designer Looks for Less' }, siteNamePart(1)],
  }),
  'Furniture dupes for less: sofas, beds, dining, lighting and decor with the look of RH, Pottery Barn and West Elm, compared side by side.'
)

// Hand-set price floor per category. The lowest raw price picks up
// accessories and junk listings ($1 "beds"), so "from $X" only counts prices at
// or above the floor. A category with no floor shows no price.
// Set at about the 5th to 10th percentile of each category's sampled prices;
// the table is in the GG-10 step-2 PR.
export const CATEGORY_PRICE_FLOORS: Readonly<Record<string, number>> = {
  'Furniture > Sofas': 300,
  'Furniture > Chairs': 125,
  'Furniture > Beds': 150,
  'Furniture > Storage': 50,
  'Furniture > Coffee Tables': 125,
  'Furniture > Dining Tables': 200,
  'Furniture > Dressers': 150,
  'Furniture > TV Stands': 125,
  'Furniture > Seating': 125,
  'Furniture > Rugs': 50,
  'Furniture > Stools': 100,
  'Furniture > Lighting': 50,
  'Furniture > Desks': 100,
  'Furniture > Nightstands': 75,
  'Furniture > Decor': 20,
  'Furniture > Bookshelves': 75,
  'Furniture > Table Lamps': 50,
  'Furniture > Chandeliers': 150,
  'Furniture > Mirrors': 60,
  'Furniture > Floor Lamps': 75,
  'Furniture > Kids': 30,
  'Furniture > Wall Art': 20,
  'Furniture > Planters': 25,
  'Furniture > Ceiling Fans': 90,
  'Fashion & Apparel': 15,
  'Fashion & Apparel > Sleepwear': 20,
  'Fashion & Apparel > Shoes': 20,
  'Fashion & Apparel > Outerwear': 25,
  'Fashion & Apparel > Tops': 12,
  'Fashion & Apparel > Bottoms': 15,
  'Fashion & Apparel > Cosmetics/Beauty': 8,
  'Fashion & Apparel > Dresses': 18,
  'Fashion & Apparel > Accessories': 8,
  'Fashion & Apparel > Bags': 18,
  'Fashion & Apparel > Swimwear': 15,
  'Fashion & Apparel > Lingerie/Underwear': 10,
  'Fashion & Apparel > Activewear': 15,
  'Fashion & Apparel > Eyewear': 12,
  'Fashion & Apparel > Jewelry': 10,
  'Fashion & Apparel > Formalwear': 25,
  'Fashion & Apparel > Watches': 12,
  'Fashion & Apparel > Luggage': 40,
  'Fashion & Apparel > Traditional Wear': 20,
}

// Proposed second rule, off until Ramin signs off: show "from $X" only when it
// is at or below the category's sampled 25th-percentile price, so a page whose
// 24 products are all high-end (Sofas from $767) shows no price instead of a
// misleadingly high one.
export const PRICE_CEILING_ENABLED = false
export const CATEGORY_PRICE_CEILINGS: Readonly<Record<string, number>> = {
  'Furniture > Sofas': 649,
  'Furniture > Chairs': 270,
  'Furniture > Beds': 399,
  'Furniture > Storage': 148,
  'Furniture > Coffee Tables': 266,
  'Furniture > Dining Tables': 538,
  'Furniture > Dressers': 288,
  'Furniture > TV Stands': 250,
  'Furniture > Seating': 330,
  'Furniture > Rugs': 120,
  'Furniture > Stools': 199,
  'Furniture > Lighting': 140,
  'Furniture > Desks': 198,
  'Furniture > Nightstands': 166,
  'Furniture > Decor': 57,
  'Furniture > Bookshelves': 162,
  'Furniture > Table Lamps': 120,
  'Furniture > Chandeliers': 420,
  'Furniture > Mirrors': 148,
  'Furniture > Floor Lamps': 170,
  'Furniture > Kids': 99,
  'Furniture > Wall Art': 57,
  'Furniture > Planters': 55,
  'Furniture > Ceiling Fans': 165,
  'Fashion & Apparel': 29,
  'Fashion & Apparel > Sleepwear': 35,
  'Fashion & Apparel > Shoes': 35,
  'Fashion & Apparel > Outerwear': 47,
  'Fashion & Apparel > Tops': 21,
  'Fashion & Apparel > Bottoms': 21,
  'Fashion & Apparel > Cosmetics/Beauty': 17,
  'Fashion & Apparel > Dresses': 32,
  'Fashion & Apparel > Accessories': 15,
  'Fashion & Apparel > Bags': 28,
  'Fashion & Apparel > Swimwear': 23,
  'Fashion & Apparel > Lingerie/Underwear': 22,
  'Fashion & Apparel > Activewear': 28,
  'Fashion & Apparel > Eyewear': 20,
  'Fashion & Apparel > Jewelry': 23,
  'Fashion & Apparel > Formalwear': 29,
  'Fashion & Apparel > Watches': 14,
  'Fashion & Apparel > Luggage': 227,
  'Fashion & Apparel > Traditional Wear': 38,
}

type PricedProduct = {
  pricing?: { price?: number | string | null; currency?: string | null } | null
}

/**
 * Lowest USD price at or above the category floor among the products the page
 * already renders, so the "from $X" is a price a visitor can see on the page.
 * Null when the category has no floor or nothing clears it.
 */
/**
 * Floor and ceiling key: "Furniture > Sofas" for a subcategory, the bare name
 * for a top category. Keyed by parent so a generic name ("Lighting",
 * "Accessories") under another vertical never inherits these values.
 */
export function categoryPriceKey(
  categoryName: string,
  parentCategoryName?: string | null
): string {
  return parentCategoryName
    ? `${parentCategoryName} > ${categoryName}`
    : categoryName
}

export function lowestPriceAtFloor(
  priceKey: string,
  products: readonly PricedProduct[],
  { applyCeiling = PRICE_CEILING_ENABLED }: { applyCeiling?: boolean } = {}
): number | null {
  const floor = CATEGORY_PRICE_FLOORS[priceKey]
  if (typeof floor !== 'number') return null
  let lowest: number | null = null
  for (const product of products) {
    const currency = (product.pricing?.currency || 'USD').toUpperCase()
    if (currency !== 'USD') continue
    const price = Number(product.pricing?.price)
    if (!Number.isFinite(price) || price < floor) continue
    if (lowest === null || price < lowest) lowest = price
  }
  if (lowest !== null && applyCeiling) {
    const ceiling = CATEGORY_PRICE_CEILINGS[priceKey]
    if (typeof ceiling !== 'number' || lowest > ceiling) return null
  }
  return lowest
}

type CategoryInput = {
  categoryName: string
  /** Products in the category, from mv_unique_product_categories. */
  count?: number | null
  /** From lowestPriceAtFloor over the products the page renders. */
  minPrice?: number | null
}

// "{Category} for Less: {N} Styles from ${min}". Drops the site name, then the
// count. Used for fashion and, until GG sends title phrases and says which
// furniture categories are top categories, for every furniture category too.
export function forLessStylesTitle({
  categoryName,
  count,
  minPrice,
}: CategoryInput): BuiltTitle {
  const n = formatTitleCount(count)
  const price = formatTitleMinPrice(minPrice)
  return buildTitle({
    name: `${categoryName} for Less`,
    parts: [
      { text: n && `: ${n} Styles`, dropRank: 2 },
      { text: price && ` from ${price}` },
      siteNamePart(1),
    ],
  })
}

// Longest candidate that fits wins. The last one is short enough for any
// realistic category name; a name long enough to overflow it is logged.
const fitDescription = (candidates: string[]): string => {
  const fit = candidates.find((d) => d.length <= DESCRIPTION_MAX_LENGTH)
  if (fit) return fit
  const last = candidates[candidates.length - 1] as string
  console.warn('[seo] category description over 155 chars', { last })
  return last
}

export function fashionCategoryDescription(categoryName: string): string {
  return fitDescription([
    `${categoryName} for less, with lookalikes of the designer styles people search for most, compared side by side on price across many stores.`,
    `${categoryName} for less, with lookalikes of the designer styles people search for most, compared side by side on price.`,
    `${categoryName} for less, with lookalikes of designer styles, compared side by side on price.`,
  ])
}

// The sheet's type-page fallback ("{Title phrase} for less from many stores,
// compared side by side on price, size and materials.") is under 120 chars
// for every name we have, so this extends it in the fashion template's shape.
export function furnitureCategoryDescription(categoryName: string): string {
  return fitDescription([
    `${categoryName} for less, with lookalikes of the designer pieces people search for most, compared side by side on price, size and materials across stores.`,
    `${categoryName} for less, with lookalikes of the designer pieces people search for most, compared side by side on price, size and materials.`,
    `${categoryName} for less, with lookalikes of designer pieces, compared side by side on price and size.`,
  ])
}

/**
 * SEO for /products/{category}. Null for verticals outside GG-10's first pass,
 * so the page keeps its current copy.
 */
export function topCategorySeo(input: CategoryInput): PageSeo | null {
  if (input.categoryName === FURNITURE_CATEGORY) return FURNITURE_HUB_SEO
  if (input.categoryName === FASHION_CATEGORY) {
    return toPageSeo(
      forLessStylesTitle(input),
      fashionCategoryDescription(input.categoryName)
    )
  }
  return null
}

/** SEO for /products/{category}/{subcategory}. Null outside furniture and fashion. */
export function subcategorySeo(
  input: CategoryInput & { parentCategoryName: string }
): PageSeo | null {
  if (input.parentCategoryName === FURNITURE_CATEGORY) {
    return toPageSeo(
      forLessStylesTitle(input),
      furnitureCategoryDescription(input.categoryName)
    )
  }
  if (input.parentCategoryName === FASHION_CATEGORY) {
    return toPageSeo(
      forLessStylesTitle(input),
      fashionCategoryDescription(input.categoryName)
    )
  }
  return null
}

/** next-seo props for a category page: the GG-10 copy, or the old copy outside it. */
export function categoryNextSeo(
  seo: PageSeo | null,
  categoryName: string
): { title: string; titleTemplate?: string; description: string } {
  return seo
    ? { title: seo.title, titleTemplate: '%s', description: seo.description }
    : {
        title: `Browse ${categoryName} products`,
        description: `Browse millions of ${categoryName} products on Dupe.com`,
      }
}
